import { serviceTypeBadge } from '@/components/features/offerings/common'
import { PriceLabel } from '@/components/features/shared/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { Account } from '@/domain/accounts/account'
import { Agreement, AgreementStatus, formatAgreementStatus, getAgreementStatusColor, UpdateAgreementData } from '@/domain/agreements/agreement'
import { Collector, CollectorStatus } from '@/domain/collectors/collector'
import { formatBillingFrequencyShort, Offering, ServiceType } from '@/domain/offerings/offering'
import { ExpensesCode, formatExpensesCode, formatSellCode } from '@/domain/parcels/parcel'
import { useCollectors } from '@/hooks/api/useCollectors'
import { useSellers } from '@/hooks/api/useSellers'
import { Paths } from '@/routes/paths'
import { calculateAgeFullStr } from '@/utils/dates'
import { useNavigate } from '@tanstack/react-router'
import { format } from 'date-fns'
import { Calendar, Clock, Edit, RefreshCw, User } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'

interface AgreementDetailsActions {
  isEditing?: boolean
  isSaving?: boolean
  onSave?: (changes: UpdateAgreementData) => void
  onCancel?: () => void
  onEdit?: () => void
}

interface AgreementDetailsData {
  userAccounts?: Account[]
  contractAccount?: Account
  isLoadingAccounts: boolean
}

interface AgreementProps {
  agreement: Agreement
  offering?: Offering
  totalBalance: number
  expensesCode?: ExpensesCode
  actions: AgreementDetailsActions
  data: AgreementDetailsData
}

export const emptyCollectors: Collector[] = []

export const AgreementDetailsHeader = (props: AgreementProps) => {
  const {
    agreement,
    offering,
    totalBalance,
    expensesCode,
    actions: { isEditing, isSaving, onSave, onCancel, onEdit },
    data: { userAccounts, isLoadingAccounts },
  } = props;

  const { data: collectors = [] } = useCollectors({ status: CollectorStatus.Active })
  const { data: sellers = [] } = useSellers({ status: CollectorStatus.Active })

  const sellersRef = useRef(sellers);
  sellersRef.current = sellers;
  const collectorsRef = useRef(collectors);
  collectorsRef.current = collectors;
  const navigate = useNavigate()
  const previousIsEditingRef = useRef(isEditing);

  const form = useForm<UpdateAgreementData>({
    defaultValues: {
      notes: '',
      sellerId: undefined,
      collectorId: undefined,
      expensesCode: undefined,
      sellCode: undefined,
    }
  })

  useEffect(() => {
    if (isEditing && !previousIsEditingRef.current) {
      form.reset({
        notes: agreement.notes || '',
        sellerId: sellersRef.current.find((s: Collector) => s.id === agreement.sellerId)?.id ?? '__none__',
        collectorId: collectorsRef.current.find((c: Collector) => c.id === agreement.collectorId)?.id ?? '__none__',
        expensesCode: expensesCode || undefined,
        sellCode: agreement.sellCode || '',
      });
      previousIsEditingRef.current = isEditing;
    } else if (!isEditing) {
      previousIsEditingRef.current = isEditing;
    }
  }, [isEditing, agreement, agreement.sellerId, agreement.collectorId, agreement.sellCode, expensesCode, form])

  const formatNameWithCode = (collectorId: string, collectors: Collector[]) => {
    if (!collectorId) return 'Sin asignar';
    const found = collectors.find((c) => c.id === collectorId);
    return found ? (found.code ? `${found.name} (${found.code})` : found.name) : 'Sin asignar';
  };

  const showPrice = agreement.status === AgreementStatus.Active || agreement.status === AgreementStatus.Pending
    || agreement.status === AgreementStatus.Terminated

  const handleSave = () => {
    onSave?.({
      notes: form.getValues('notes') || undefined,
      sellerId: form.getValues('sellerId') === '__none__' ? null : form.getValues('sellerId'),
      collectorId: form.getValues('collectorId') === '__none__' ? null : form.getValues('collectorId'),
      expensesCode: form.getValues('expensesCode') || undefined,
      sellCode: form.getValues('sellCode') || undefined,
    })
  }

  return (
    <CardHeader className='p-4 md:p-6'>
      <div className="flex items-center justify-between mb-2">
        {offering?.serviceType && <>{serviceTypeBadge(offering.serviceType)}</>}
        <Badge className={getAgreementStatusColor(agreement.status)}>
          {formatAgreementStatus(agreement.status)}
        </Badge>
      </div>
      <div className="flex items-center justify-between">
        <div className='flex flex-col items-start gap-2'>
          <CardTitle>{agreement.offeringName}</CardTitle>
          <p className="font-mono text-sm text-muted-foreground">Código: {offering?.code || '-'}</p>
        </div>
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Button variant="info" size="sm" className="h-7 text-[10px] font-bold uppercase tracking-tight px-3" onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1" />
                  Guardando
                </>
              ) : 'Guardar'}
            </Button>
            {onCancel && (
              <Button variant="outline" size="sm" className="h-7 text-[10px] font-bold uppercase tracking-tight px-3" onClick={onCancel} disabled={isSaving}>
                Cancelar
              </Button>
            )}
          </div>
        ) : (
          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={onEdit} title='Editar datos del contrato'>
            <Edit className="h-4 w-4 text-warning" />
          </Button>
        )}
      </div>
      <div className="grid gap-x-3 gap-y-5 sm:grid-cols-2 lg:grid-cols-4 p-5 rounded-xl bg-muted/30 border border-border/30">
        <div className="flex flex-col gap-1 sm:col-span-2 lg:col-span-2">
          <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Cliente</span>
          <div className="flex flex-col gap-1.5">
            <div className="w-full flex justify-between items-center gap-1 cursor-pointer w-fit bg-background hover:bg-gray-100 border border-border/50 rounded-xl px-2 py-1.5 transition-colors shadow-sm"
              onClick={() => navigate({ to: Paths.Management.Clients + `/${agreement.userId}` })}>
              <div className="flex items-center gap-1">
                <User className="h-4 w-4 text-primary/60 flex-shrink-0" />
                <span className="font-semibold text-sm">{agreement.userName}</span>
              </div>
              <span className="font-mono text-xs text-muted-foreground">Nº{agreement.userCode}</span>
            </div>
            {userAccounts && userAccounts.length > 0 && totalBalance !== 0 && (
              <div className="text-xs flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-border/50 bg-background/50 w-fit">
                <span className="text-muted-foreground font-medium">Saldo Total:</span>
                <span className={`${totalBalance >= 0 ? 'text-green-600' : 'text-red-600'} font-bold`}>
                  ${totalBalance.toLocaleString('es-AR')}
                </span>
              </div>
            )}
            {isLoadingAccounts && <Skeleton className="h-4 w-24 mt-0.5 rounded-full" />}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:col-span-2 lg:col-span-2">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Plan</span>
            {isEditing ? (
              <div className="flex items-center gap-2">
                <Controller
                  control={form.control}
                  name="sellCode"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="h-7 text-xs">
                        <SelectValue placeholder="Seleccionar plan" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="U">U - Urgencia</SelectItem>
                        <SelectItem value="P">P - Prenecesidad</SelectItem>
                        <SelectItem value="T">T - Traslado</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            ) : (
              <div className="font-mono text-xs text-muted-foreground">{formatSellCode(agreement.sellCode!)}</div>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Monto final</span>
            <div className="flex items-center gap-2">
              {(showPrice) ?
                <div className="flex items-center gap-1 text-sm">
                  <PriceLabel price={agreement.amount!} />
                  <span className='font-mono text-xs text-muted-foreground'>{formatBillingFrequencyShort(agreement.billingFrequency!)}</span>
                </div>
                : <span className="text-muted-foreground font-medium">-</span>
              }
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Vendedor</span>
            {isEditing ? (
              <div className="flex items-center gap-2">
                <Controller
                  control={form.control}
                  name="sellerId"
                  render={({ field }) => (
                    <Select value={field.value!!} onValueChange={field.onChange}>
                      <SelectTrigger className="h-7 text-xs">
                        <SelectValue placeholder="Seleccionar vendedor" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Sin vendedor asignado</SelectItem>
                        {sellers.map((seller) => (
                          <SelectItem key={seller.id} value={seller.id}>
                            {seller.name} ({seller.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <User className="h-4 w-4 text-primary/60 flex-shrink-0" />
                <span className='text-xs text-muted-foreground'>{formatNameWithCode(agreement.sellerId!, sellers)}</span>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Cobrador</span>
            {!isEditing && <div className="flex items-center gap-1">
              <User className="h-4 w-4 text-primary/60 flex-shrink-0" />
              <span className='text-xs text-muted-foreground'>{formatNameWithCode(agreement.collectorId!, collectors)}</span>
            </div>}
            {isEditing && <div className="flex items-center gap-2">
              <Controller
                control={form.control}
                name="collectorId"
                render={({ field }) => (
                  <Select value={field.value!!} onValueChange={field.onChange}>
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue placeholder="Seleccionar cobrador" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sin cobrador asignado</SelectItem>
                      {collectors.map((collector) => (
                        <SelectItem key={collector.id} value={collector.id}>
                          {collector.name} ({collector.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 pt-5 border-t sm:col-span-2 lg:col-span-4">
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Fecha de inicio</span>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary/60 flex-shrink-0" />
            <span className="text-sm font-medium">{format(new Date(agreement.startDate), "dd/MM/yyyy")}</span>
          </div>
        </div>

        {agreement.endDate && (
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
              {offering?.serviceType === ServiceType.CREMACIONES ? 'Fecha de cremación' : 'Fecha de finalización'}
            </span>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary/60 flex-shrink-0" />
              <span className="text-sm font-medium">{format(new Date(agreement.endDate), "dd/MM/yyyy")}</span>
            </div>
          </div>
        )}

        {offering?.serviceType !== ServiceType.CREMACIONES && (
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Duración</span>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary/60 flex-shrink-0" />
              <span className='text-sm font-medium'>
                {calculateAgeFullStr(new Date(agreement.startDate), agreement.endDate && new Date(agreement.endDate))}
              </span>
            </div>
          </div>
        )}

        {isEditing && offering?.serviceType === ServiceType.PARCELAS ? (
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Expensas</span>
            <div className="flex items-center gap-2">
              <Controller
                control={form.control}
                name="expensesCode"
                render={({ field }) => (
                  <Select value={field.value || ''} onValueChange={field.onChange}>
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue placeholder="Sin código de expensas asignado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="U">U - En Uso</SelectItem>
                      <SelectItem value="B">B - Urna cineraria</SelectItem>
                      <SelectItem value="S">S - Perpetuidad</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>
        ) : offering?.serviceType === ServiceType.PARCELAS ? (
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Expensas</span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{expensesCode} - {formatExpensesCode(expensesCode)}</span>
            </div>
          </div>
        ) : <></>}
      </div>

      <div className="flex flex-col gap-2 mt-6">
        <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider px-1">Notas del Contrato</span>
        {isEditing ? (
          <Controller
            control={form.control}
            name="notes"
            render={({ field }) => (
              <Textarea
                {...field}
                value={field.value || ''}
                className="min-h-[60px] rounded-xl border-border/60 bg-muted/10"
              />
            )}
          />
        ) : (
          agreement.notes ? (
            <div className="rounded-xl border border-border/60 bg-muted/10 p-4 text-sm leading-relaxed text-muted-foreground">
              {agreement.notes}
            </div>
          ) : (
            <div className="rounded-xl border border-border/60 bg-muted/10 p-4 text-sm leading-relaxed text-muted-foreground italic">
              Sin notas
            </div>
          )
        )}
      </div>
    </CardHeader>
  )
}
